import { useState, useEffect, useCallback } from 'react';
import izenuAPI from '../../services/izenuAPI';

const WEB_APP = 'https://app.izenu.ao';

/** Copies text to the clipboard and confirms it in place. */
function CopyButton({ value, label = 'Copiar link', style }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(done).catch(() => {});
      return;
    }
    // painel is served over HTTPS, but keep a fallback for local http testing.
    const el = document.createElement('textarea');
    el.value = value;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    try { document.execCommand('copy'); done(); } catch (e) { /* ignore */ }
    document.body.removeChild(el);
  }, [value]);

  return (
    <button
      onClick={copy}
      title={value}
      style={{
        padding: '4px 12px', borderRadius: 4,
        border: `1px solid ${copied ? '#2E7D32' : '#6C3FC5'}`,
        backgroundColor: copied ? '#E8F5E9' : 'transparent',
        color: copied ? '#2E7D32' : '#6C3FC5',
        fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {copied ? 'Copiado!' : label}
    </button>
  );
}

/**
 * Every entry point the web app exposes. `sample` is filled with a real code
 * when the admin picks one from the table.
 */
const LINK_PATTERNS = [
  {
    build: () => `${WEB_APP}/codigo`,
    what: 'Página da campanha. Mostra o código configurado no web app (ACTIVE_PROMO) — é o link para pôr no anúncio.',
  },
  {
    build: (code) => `${WEB_APP}/codigo/${code}`,
    what: 'Mesma página, mas para um código específico. Use este para campanhas paralelas.',
    needsCode: true,
  },
  {
    build: (code) => `${WEB_APP}/codigo?code=${code}`,
    what: 'Equivalente ao anterior, em query string — útil quando o link é montado por outra ferramenta.',
    needsCode: true,
  },
  {
    build: (code) => `${WEB_APP}/signup?promo=${code}`,
    what: 'Salta a página da campanha e vai direto ao registo, com o campo do código já preenchido.',
    needsCode: true,
  },
  {
    build: () => `${WEB_APP}/signup?promo=1`,
    what: 'Registo com o campo do código aberto mas vazio, para a pessoa escrever o que tiver.',
  },
  {
    build: () => `${WEB_APP}/premium?plan=mensal`,
    what: 'Abre o checkout já no plano mensal (2.250 Kz/mês, ou $2,99 no cartão). Aceita mensal, monthly, month ou mes.',
  },
  {
    build: () => `${WEB_APP}/premium?plan=semanal`,
    what: 'Abre o checkout no plano semanal (592 Kz/semana, só Multicaixa). Aceita semanal, weekly, week ou semana.',
  },
  {
    build: () => `${WEB_APP}/premium`,
    what: 'Página de planos, sem abrir nada.',
  },
  {
    build: () => `${WEB_APP}/login?next=/premium`,
    what: 'Login e devolve a pessoa ao endereço indicado. Só aceita caminhos do próprio site.',
  },
];

export default function PromoCodes() {
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState('');
  const [newDays, setNewDays] = useState(7);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [sampleCode, setSampleCode] = useState('');

  const fetchCodes = useCallback(async () => {
    try {
      const r = await izenuAPI.get('/admin/promo_code/list.php');
      if (r.data.status === 'success') {
        const list = r.data.data || [];
        setCodes(list);
        setSampleCode(prev => prev || (list[0] ? list[0].code : ''));
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreate = useCallback(async () => {
    if (!newCode.trim()) return;
    setCreating(true);
    setError('');
    try {
      const r = await izenuAPI.post('/admin/promo_code/create.php', {
        code: newCode.trim().toUpperCase(),
        days: newDays,
      });
      if (r.data.status === 'success') {
        setNewCode('');
        setNewDays(7);
        fetchCodes();
      } else {
        setError(r.data.message || 'Erro ao criar código.');
      }
    } catch (err) {
      setError('Erro ao criar código.');
    } finally {
      setCreating(false);
    }
  }, [newCode, newDays, fetchCodes]);

  const handleDelete = useCallback(async (id, code) => {
    if (!window.confirm(`Eliminar o código "${code}"?`)) return;
    try {
      const r = await izenuAPI.post('/admin/promo_code/delete.php', { id });
      if (r.data.status === 'success') {
        setCodes(prev => prev.filter(c => c.id !== id));
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  return (
    <div style={{ padding: 40 }}>
      <h2 style={{ color: '#333', marginBottom: 10, fontSize: 22, fontWeight: 700 }}>
        Códigos Promocionais
      </h2>
      <p style={{ color: '#666', marginBottom: 30, fontSize: 14 }}>
        Cria e gere códigos que activam Premium gratuitamente.
      </p>

      {/* Create form */}
      <div style={{
        display: 'flex', gap: 12, alignItems: 'flex-end',
        marginBottom: 30, flexWrap: 'wrap',
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Código</label>
          <input
            type="text"
            value={newCode}
            onChange={e => setNewCode(e.target.value.toUpperCase())}
            placeholder="IZENU7DIAS"
            style={{
              padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
              fontSize: 14, width: 200, textTransform: 'uppercase',
            }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>Dias de Premium</label>
          <input
            type="number"
            value={newDays}
            onChange={e => setNewDays(Math.max(1, parseInt(e.target.value) || 1))}
            min={1}
            style={{
              padding: '8px 12px', borderRadius: 6, border: '1px solid #ddd',
              fontSize: 14, width: 100,
            }}
          />
        </div>
        <button
          onClick={handleCreate}
          disabled={creating || !newCode.trim()}
          style={{
            padding: '8px 20px', borderRadius: 6, border: 'none',
            backgroundColor: '#6C3FC5', color: '#fff', fontSize: 14,
            fontWeight: 600, cursor: 'pointer', opacity: creating ? 0.6 : 1,
          }}
        >
          {creating ? 'A criar...' : 'Criar Código'}
        </button>
      </div>

      {error && (
        <p style={{ color: '#E5004D', fontSize: 13, marginBottom: 16 }}>{error}</p>
      )}

      {/* Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
            <th style={{ padding: '10px 8px', color: '#666', fontSize: 12, fontWeight: 600 }}>CÓDIGO</th>
            <th style={{ padding: '10px 8px', color: '#666', fontSize: 12, fontWeight: 600 }}>DIAS</th>
            <th style={{ padding: '10px 8px', color: '#666', fontSize: 12, fontWeight: 600 }}>USOS</th>
            <th style={{ padding: '10px 8px', color: '#666', fontSize: 12, fontWeight: 600 }}>CRIADO EM</th>
            <th style={{ padding: '10px 8px', color: '#666', fontSize: 12, fontWeight: 600 }}>AÇÕES</th>
          </tr>
        </thead>
        <tbody>
          {codes.length === 0 ? (
            <tr>
              <td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 14 }}>
                Nenhum código promocional criado.
              </td>
            </tr>
          ) : (
            codes.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px', fontWeight: 600, fontSize: 14, fontFamily: 'monospace' }}>
                  {c.code}
                </td>
                <td style={{ padding: '12px 8px', fontSize: 14 }}>
                  {c.days} {c.days === 1 ? 'dia' : 'dias'}
                </td>
                <td style={{ padding: '12px 8px', fontSize: 14 }}>
                  <span style={{
                    backgroundColor: c.usage_count > 0 ? '#E8F5E9' : '#f5f5f5',
                    color: c.usage_count > 0 ? '#2E7D32' : '#999',
                    padding: '2px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600,
                  }}>
                    {c.usage_count}
                  </span>
                </td>
                <td style={{ padding: '12px 8px', fontSize: 13, color: '#666' }}>
                  {new Date(c.created_at).toLocaleDateString('pt-PT')}
                </td>
                <td style={{ padding: '12px 8px', display: 'flex', gap: 8 }}>
                  <CopyButton value={`${WEB_APP}/codigo/${c.code}`} />
                  <button
                    onClick={() => setSampleCode(c.code)}
                    style={{
                      padding: '4px 12px', borderRadius: 4,
                      border: `1px solid ${sampleCode === c.code ? '#6C3FC5' : '#ddd'}`,
                      backgroundColor: sampleCode === c.code ? '#F3EEFF' : 'transparent',
                      color: sampleCode === c.code ? '#6C3FC5' : '#666',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    Ver links
                  </button>
                  <button
                    onClick={() => handleDelete(c.id, c.code)}
                    style={{
                      padding: '4px 12px', borderRadius: 4, border: '1px solid #E5004D',
                      backgroundColor: 'transparent', color: '#E5004D', fontSize: 12,
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Reference: every link the web app understands */}
      <div style={{
        marginTop: 40, padding: 24, borderRadius: 10,
        border: '1px solid #eee', backgroundColor: '#fafafa',
      }}>
        <h3 style={{ color: '#333', fontSize: 17, fontWeight: 700, margin: 0 }}>
          Links do web app
        </h3>
        <p style={{ color: '#666', fontSize: 13, margin: '6px 0 20px' }}>
          Endereços a usar em anúncios, QR codes, email ou notificações.
          {sampleCode
            ? <> Os exemplos abaixo usam o código <strong style={{ fontFamily: 'monospace' }}>{sampleCode}</strong> — carrega em “Ver links” noutra linha da tabela para trocar.</>
            : ' Cria um código acima para veres os exemplos preenchidos.'}
        </p>

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {LINK_PATTERNS
              .filter(p => !p.needsCode || sampleCode)
              .map(p => {
                const url = p.build(sampleCode);
                return (
                  <tr key={url} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '12px 8px 12px 0', width: '50%' }}>
                      <code style={{
                        fontFamily: 'monospace', fontSize: 13, color: '#6C3FC5',
                        wordBreak: 'break-all',
                      }}>
                        {url}
                      </code>
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: 13, color: '#666', lineHeight: 1.5 }}>
                      {p.what}
                    </td>
                    <td style={{ padding: '12px 0 12px 8px', width: 1 }}>
                      <CopyButton value={url} label="Copiar" />
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>

        <p style={{ color: '#999', fontSize: 12, marginTop: 18, marginBottom: 0, lineHeight: 1.6 }}>
          O checkout do <strong>/premium</strong> só abre para quem tem sessão iniciada e ainda não é
          Premium; um visitante vê primeiro o botão de iniciar sessão e volta ao mesmo plano depois de
          entrar. Um código só pode ser usado uma vez por conta.
        </p>
      </div>
    </div>
  );
}
